import {GuideUtils} from "../guide-utils";
import Shepherd from "shepherd.js";
import {decodeHTML} from "../../../../app";
import {
    navigate,
    getPathName,
} from '@ontotext/workbench-api';
import {NEAREST, SMOOTH} from "../model/guides";
import {LoggerProvider} from "../../core/services/logger-provider";

export const GUIDE_ID = 'shepherd.guide_id';
export const GUIDE_CURRENT_STEP_ID = 'shepherd.guide.current.step.id';
export const GUIDE_STEP_HISTORY = 'shepherd.guide.step.history';
export const GUIDE_PAUSE = 'shepherd.guide.pause';

const modules = ['graphdb.framework.utils.localstorageadapter'];
const logger = LoggerProvider.logger;

angular
    .module('graphdb.framework.guides.shepherd.services', modules)
    .service('ShepherdService', ShepherdService);

ShepherdService.$inject = ['$translate', 'LocalStorageAdapter', 'LSKeys', '$interpolate', 'toastr', '$compile', 'ModalService'];

/**
 * Service (wrapper) of library  <a href="https://shepherdjs.dev/docs/">shepherd</a>.
 * @param $translate
 * @param LocalStorageAdapter
 * @param LSKeys
 * @param $interpolate
 * @param toastr
 * @param $compile
 * @param ModalService
 * @constructor
 */
function ShepherdService($translate, LocalStorageAdapter, LSKeys, $interpolate, toastr, $compile, ModalService) {
    this.guideCancelSubscription = undefined;
    this.guideCompleteSubscription = undefined;
    this.guideAutostarted = null;

    this.onPause = () => {
    };
    this.onCancel = () => {
    };

    /**
     * Creates and starts a guide.
     * @param {string} guideId - a unique ID that identifies the guide.
     * @param {*} stepsDescriptions - array with core steps. Format of a step is:
     * <pre>
     *     {
     *       title: string,
     *       content: string,
     *       elementSelector: string,
     *       url: string,
     *       placement: string,
     *       type: string,
     *       maxWaitTime: number,
     *       canBePaused: boolean,
     *       onNextClick: function,
     *       onPreviousClick: function,
     *       beforeShowPromise: function
     *     }
     * </pre>
     *
     * <ul>
     *     <li>
     *         <b>title</b> - the title of the step. It will be shown as header of popover dialog;
     *     </li>
     *     <li>
     *         <b>content</b> - the content of the step. It will be shown as content of popover dialog;
     *     </li>
     *     <li>
     *         <b>elementSelector</b> - the selector of the element where popover dialog will be placed.
     *     </li>
     *     <li>
     *         <b>url</b> - the url of the page where the element is present;
     *     </li>
     *     <li>
     *         <b>placement</b> - where popover dialog to be placed. Possible values:
     *         <ul>
     *           <li>auto</li>
     *           <li>auto-start</li>
     *           <li>auto-end</li>
     *           <li>top</li>
     *           <li>top-start</li>
     *           <li>top-end</li>
     *           <li>bottom</li>
     *           <li>bottom-start</li>
     *           <li>bottom-end</li>
     *           <li>right</li>
     *           <li>right-start</li>
     *           <li>right-end</li>
     *           <li>left</li>
     *           <li>left-start</li>
     *           <li>left-end</li>
     *         </ul>
     *     </li>
     *     <li><b>type</b> - the type of step. Possible values:
     *       <ul>
     *           <li>
     *               <b>clickable-element</b> - the element is clickable like link, button... Client of workbench can click on this element.
     *           </li>
     *           <li>
     *               <b>read-only-element</b> - the element is readable. Client of workbench can only read this component.
     *           </li>
     *           <li>
     *               <b>input-element</b> - the element is some kind ot input like text, text area ... . Client of workbench can enter data in the element.
     *           </li>
     *        </ul>
     *     </li>
     *     <li>
     *         <b>maxWaitTime</b> - some element need more time to be present on the page. This is the max waiting time element to be visible
     *         on the page. Default value is 3 second.
     *     </li>
     *     <li>
     *         <b>canBePaused</b> - some element need user interaction with some other element on the page before appear. Step which uses such
     *         element can be persist when guide is paused. For example if step is "Click on sub menu "Repositories" of menu "Setup""
     *     </li>
     *     <li>
     *         <b>onNextClick</b> - a function which will be executed when nex button is clicked.
     *     </li>
     *     <li>
     *         <b>onPreviousClick</b> - a function which will be executed when previous button is clicked.
     *     </li>
     *     <li>
     *         <b>beforeShowPromise</b> - a function that returns a promise. When the promise resolves, the rest of the show code for the step will execute.
     *         Default implementation is wait <code>maxWaitTime</code> element with <code>elementSelector</code> to be visible.
     *     </li>
     *
     * </ul>
     * @param {string} startStepId - start step id.
     * @param isAutoStarted - whether the guide was automatically started
     * @param {boolean} clearHistory - if true will clear guide step history.
     */
    this.startGuide = (guideId, stepsDescriptions, startStepId, isAutoStarted = false, clearHistory = true) => {
        if (isAutoStarted && this._isAutostartDisabledForGuide(guideId)) {
            return;
        } else {
            this.guideAutostarted = isAutoStarted;
        }

        if (clearHistory) {
            LocalStorageAdapter.remove(GUIDE_STEP_HISTORY);
        }

        if (!stepsDescriptions) {
            return;
        }
        const guide = this._createGuide(guideId);
        this._addGuideSteps(guide, stepsDescriptions);
        this._startGuide(guide, startStepId);
    };

    /**
     * Starts guide from the step where it was paused.
     * @param {string} guideId
     * @param {*} stepsDescriptions
     * @param {string} startStepId
     */
    this.resumeGuide = (guideId, stepsDescriptions, startStepId) => {
        LocalStorageAdapter.set(GUIDE_PAUSE, false);
        this.startGuide(guideId, stepsDescriptions, startStepId, false);
    };

    this.isActive = () => {
        return Shepherd.activeTour && !this.isPaused();
    };

    this.isScrollingAllowed = () => {
        const allowScroll = Shepherd.activeTour?.getCurrentStep()?.options?.allowScroll;
        return !this.isActive() || allowScroll;
    };

    this.isPaused = () => {
        return LocalStorageAdapter.get(GUIDE_PAUSE) === 'true';
    };

    this.getGuideId = () => {
        return LocalStorageAdapter.get(GUIDE_ID);
    };

    this.getCurrentStepId = () => {
        return LocalStorageAdapter.get(GUIDE_CURRENT_STEP_ID);
    };

    this.subscribeToGuideCancel = (oncancel) => {
        if (this._isFunction(oncancel)) {
            this.onCancel = oncancel;
        }
    };

    this.subscribeToGuidePause = (onPause) => {
        if (this._isFunction(onPause)) {
            this.onPause = onPause;
        }
    };

    this._isAutostartDisabledForGuide = (guideId) => {
        const guideAutostartState = LocalStorageAdapter.get(LSKeys.GUIDES_AUTOSTART) || {};
        return guideAutostartState[guideId]?.disabled;
    };

    this._disableAutostartForGuide = (guideId) => {
        const guideAutostartState = LocalStorageAdapter.get(LSKeys.GUIDES_AUTOSTART) || {};
        guideAutostartState[guideId] = {disabled: true};
        LocalStorageAdapter.set(LSKeys.GUIDES_AUTOSTART, JSON.stringify(guideAutostartState));
    };

    /**
     * Creates a guide.
     * @param {string} guideId - a unique ID that identifies the guide.
     * @return {Shepherd.Tour} - created guide.
     */
    this._createGuide = (guideId) => {
        this._subscribeToGuideCanceled();
        return new Shepherd.Tour({
            name: guideId,
            useModalOverlay: true,
            confirmCancel: this._confirmGuideCancel,
            defaultStepOptions: {
                classes: 'shadow-md bg-purple-dark',
                scrollTo: {
                    behavior: SMOOTH,
                    block: NEAREST,
                    inline: NEAREST,
                },
            },
        });
    };

    this._confirmGuideCancel = () => {
        const guide = Shepherd.activeTour;
        const currentStep = guide.getCurrentStep();

        if (currentStep.options.isLastStep) {
            this._completeGuide(guide);
            return;
        }

        guide.hide();

        return ModalService.openCustomModal({
            templateUrl: 'js/angular/guides/templates/modal-confirm-exit.html',
            title: decodeHTML($translate.instant('guides.confirm-cancel-modal.title')),
            message: decodeHTML($translate.instant('guides.confirm-cancel-modal.content')),
            warning: true,
            controller: ['$scope', '$uibModalInstance', '$sce', 'config', ConfirmExitModalCtrl],
            hasDontShowAgain: this.guideAutostarted,
        }).result
            .then((disableAutoStart) => {
                // Exit of guide is confirmed
                if (disableAutoStart) {
                    // User choose not to auto start the guide again
                    this._disableAutostartForGuide(guide.options.name);
                }
                return true;
            })
            .catch(() => {
                guide.show(currentStep.id);
            });

        function ConfirmExitModalCtrl($scope, $uibModalInstance, $sce, config) {
            $scope.cancelButtonKey = config.cancelButtonKey;
            $scope.hasDontShowAgain = config.hasDontShowAgain;
            $scope.title = config.title;
            $scope.message = $sce.trustAsHtml(config.message);

            $scope.exit = function() {
                $uibModalInstance.close(false);
            };

            $scope.dontShowAgain = function() {
                $uibModalInstance.close(true);
            };

            $scope.cancel = function() {
                $uibModalInstance.dismiss();
            };
        }
    };

    /**
     * Transforms <code>stepsDescriptions</code> to Shepherd Tour steps and add it to <code>guide<code>.
     * @param {Shepherd.Tour} guide - the guide.
     * @param {*} stepsDescriptions - array with steps descriptions. The format of a description is:
     *
     * <pre>
     *     {
     *       title: string,
     *       content: string,
     *       elementSelector: string,
     *       url: string,
     *       placement: string,
     *       type: string,
     *       maxWaitTime: number,
     *       canBePaused: boolean,
     *       onNextClick: function,
     *       onNextValidate: function,
     *       onPreviousClick: function,
     *       beforeShowPromise: function
     *     }
     * </pre>
     *
     * <ul>
     *     <li>
     *         <b>title</b> - the title of the step. It will be shown as header of popover dialog;
     *     </li>
     *     <li>
     *         <b>content</b> - the content of the step. It will be shown as content of popover dialog;
     *     </li>
     *     <li>
     *         <b>elementSelector</b> - the selector of the element where popover dialog will be placed.
     *     </li>
     *     <li>
     *         <b>url</b> - the url of the page where the element is present;
     *     </li>
     *     <li>
     *         <b>placement</b> - where popover dialog to be placed. Possible values:
     *         <ul>
     *           <li>auto</li>
     *           <li>auto-start</li>
     *           <li>auto-end</li>
     *           <li>top</li>
     *           <li>top-start</li>
     *           <li>top-end</li>
     *           <li>bottom</li>
     *           <li>bottom-start</li>
     *           <li>bottom-end</li>
     *           <li>right</li>
     *           <li>right-start</li>
     *           <li>right-end</li>
     *           <li>left</li>
     *           <li>left-start</li>
     *           <li>left-end</li>
     *         </ul>
     *     </li>
     *     <li><b>type</b> - the type of step. Possible values:
     *       <ul>
     *           <li>
     *               <b>clickable-element</b> - the element is clickable like link, button... Client of workbench can click on this element.
     *           </li>
     *           <li>
     *               <b>read-only-element</b> - the element is readable. Client of workbench can only read this component.
     *           </li>
     *           <li>
     *               <b>input-element</b> - the element is some kind ot input like text, text area ... . Client of workbench can enter data in the element.
     *           </li>
     *        </ul>
     *     </li>
     *     <li>
     *         <b>maxWaitTime</b> - some element need more time to be present on the page. This is the max waiting time element to be visible
     *         on the page. Default value is 3 second.
     *     </li>
     *     <li>
     *         <b>canBePaused</b> - some element need user interaction with some other element on the page before appear. Step which uses such
     *         element can be persisted when guide is paused. For example if step is "Click on sub menu "Repositories" of menu "Setup""
     *     </li>
     *     <li>
     *         <b>onNextClick</b> - function which will be executed when next button is clicked.
     *     </li>
     *     <li>
     *         <b>onNextValidate</b> - function that determines whether pressing the next button advances the step
     *     </li>
     *      <li>
     *         <b>onPreviousClick</b> - function which will be executed when previous button is clicked.
     *     </li>
     *     <li>
     *         <b>beforeShowPromise</b> - a function that returns a promise. When the promise resolves, the rest of the show code for the step will execute.
     *         Default implementation is wait <code>maxWaitTime</code> element with <code>elementSelector</code> to be visible.
     *     </li>
     * </ul>
     */
    this._addGuideSteps = (guide, stepsDescriptions) => {
        // Check if stepsDescriptions is not empty. This mean there is first step. The first step has not previous one.
        if (stepsDescriptions.length > 0) {
            guide.addStep(this._getFirstStep(guide, stepsDescriptions));
        }

        // Check if stepsDescriptions has more than 2 elements. This mean there are middle steps. The middle steps have previous and next steps.
        if (stepsDescriptions.length > 2) {
            // Process all steps without first and last one.
            for (let index = 1; index < stepsDescriptions.length - 1; index++) {
                guide.addStep(this._getMiddleStep(guide, stepsDescriptions, index));
            }
        }

        // Check if stepsDescriptions more than 1 element, this mean that we have process last step. The last step has no next step.
        if (stepsDescriptions.length > 1) {
            guide.addStep(this._getLastStep(guide, stepsDescriptions[stepsDescriptions.length - 1]));
        }
    };

    /**
     * Starts <code>guide</code> from <code>startStepId</code>
     * @param {Shepherd.Tour} guide - the guid to be started.
     * @param {string} startStepId - star step id. If it is not present the <code>guide</code> will be started from beginning.
     * @private
     */
    this._startGuide = (guide, startStepId) => {
        LocalStorageAdapter.remove(GUIDE_PAUSE);

        let stepIndex = guide.steps.findIndex(((value) => value.options.id === startStepId));
        if (stepIndex < 0) {
            stepIndex = 0;
        }
        const step = guide.steps[stepIndex];
        const url = step.options.url;
        if (url && url !== getPathName()) {
            navigate(url);
        }

        if (step.options.attachTo) {
            GuideUtils.getOrWaitFor(step.options.attachTo.element, step.options.maxWaitTime)
                .then(() => guide.show(stepIndex))
                .catch((error) => {
                    logger.error(error);
                    toastr.error($translate.instant('guide.start.unexpected.error.message'));
                });
        } else {
            guide.show(stepIndex);
        }
    };

    this._subscribeToGuideCanceled = () => {
        const guideEndHandler = ({tour}) => {
            this._clearLocalStorage();
            const currentStep = tour.getCurrentStep();
            if (currentStep && this._isFunction(currentStep.hide)) {
                // Some steps have state and when shepherd library hides them the "hide()" function is called. This function cleaned the state
                // of the steps. When we are on step which has state and click on cancel button the library don't call this function.
                // So we call it manually. For example see "select-repository" plugin it has listener which must be removed when step is hided.
                currentStep.hide();
            }
            this.guideAutostarted = null;
            if (this.onCancel) {
                this.onCancel();
            }
        };
        if (!this.guideCancelSubscription) {
            this.guideCancelSubscription = Shepherd.on('cancel', guideEndHandler);
        }
        if (!this.guideCompleteSubscription) {
            this.guideCompleteSubscription = Shepherd.on('complete', guideEndHandler);
        }
    };

    /**
     * Creates first step from <code>stepsDescriptions</code>.
     * @param {Shepherd.Tour} guide - the guide.
     * @param {*} stepsDescriptions - array with descriptions of steps.
     * @return {Shepherd.Step} the first step.
     * @private
     */
    this._getFirstStep = function(guide, stepsDescriptions) {
        const nextStepDescription = stepsDescriptions.length > 1 ? stepsDescriptions[1] : undefined;
        return this._toGuideStep(guide, undefined, stepsDescriptions[0], nextStepDescription);
    };

    /**
     * Creates middle step from <code>stepsDescriptions</code>.
     * @param {Shepherd.Tour} guide - the guide.
     * @param {*} stepsDescriptions - array with descriptions of steps.
     * @param {number} indexOfProcessedStep - index of step which have to be created.
     * @return {Shepherd.Step} the created step.
     * @private
     */
    this._getMiddleStep = (guide, stepsDescriptions, indexOfProcessedStep) => {
        const currentStep = stepsDescriptions[indexOfProcessedStep];
        if (currentStep.lastStep) {
            return this._getLastStep(guide, currentStep);
        }
        return this._toGuideStep(guide, stepsDescriptions[indexOfProcessedStep - 1], currentStep, stepsDescriptions[indexOfProcessedStep + 1]);
    };

    /**
     * Creates last step from <code>stepsDescriptions</code>.
     * @param {Shepherd.Tour} guide - the guide.
     * @param {*} lastStepDescription - description of last step.
     * @return {Shepherd.Step} the last step.
     * @private
     */
    this._getLastStep = (guide, lastStepDescription) => {
        const step = this._toGuideStep(guide, null, lastStepDescription);
        step.buttons.push(this._getBackToGuidesButton(guide));
        step.buttons.push(this._getCancelButton(guide));
        return step;
    };

    this._getBackToGuidesButton = (guide) => {
        return this._getButton($translate.instant('back.to.guides.btn'), () => this._backToGuidesButton(guide), true);
    };


    this._getCancelButton = (guide) => {
        const cancelButton = this._getButton($translate.instant('common.close'), () => this._completeGuide(guide), true);
        cancelButton.classes += ' shepherd-cancel-button';
        return cancelButton;
    };

    this._backToGuidesButton = (guide) => {
        navigate('guides');
        this._completeGuide(guide);
    };

    /**
     * Completes teh guide
     * @param guide
     * @private
     */
    this._completeGuide = (guide) => {
        if (this.guideAutostarted) {
            this._disableAutostartForGuide(guide.options.name);
        }
        guide.complete();
    };

    /**
     * Abort the guide due to an error.
     * @param {Shepherd.Tour} guide
     * @private
     */
    this._abortGuide = (guide) => {
        toastr.error($translate.instant('guide.unexpected.error.message'));
        guide.options.confirmCancel = false;
        guide.cancel();
    };

    /**
     * Converts <code>currentStepDescription</code> to Shepherd Tour step.
     * @param {Shepherd.Tour} guide - the guide.
     * @param {*} previousStepDescription - the previous step description.
     * @param {*} currentStepDescription - processed step description.
     * @param {*} nextStepDescription - the next step description.
     * @return {Shepherd.Step} created step.
     * @private
     */
    this._toGuideStep = (guide, previousStepDescription, currentStepDescription, nextStepDescription) => {
        const step = this._toBaseGuideStep(guide, $translate, currentStepDescription, () => this._updateLocalStorage());
        const buttons = [];
        if (currentStepDescription.skipPoint) {
            buttons.push(this._getSkipButton(guide, currentStepDescription.skipButtonLabel));
        }

        if (!this._isDisablePreviousFlow(currentStepDescription) && previousStepDescription) {
            buttons.push(this._getPreviousButton(guide));
        }

        if (!this._isDisableNextFlow(currentStepDescription) && nextStepDescription) {
            buttons.push(this._getNextButton(guide, currentStepDescription, nextStepDescription));
        }

        step.buttons = buttons;
        return step;
    };

    this._getPauseButton = (guide) => {
        return this._getButton($translate.instant('pause.btn'), () => this._pauseGuide(guide), true);
    };

    this._getSkipButton = (guide, message) => {
        const skipBtnLabel = GuideUtils.translateLocalMessage($translate, $interpolate, message, {});
        return this._getButton(skipBtnLabel, () => this._skipSteps(guide), true);
    };

    this._skipSteps = (guide) => {
        const currentStep = guide.getCurrentStep();
        const currentStepIndex = guide.steps.findIndex((step) => currentStep.id === step.id);
        this._startGuide(guide, this._getNextSkipPointId(guide.steps, currentStepIndex));
    };

    this._getNextSkipPointId = (steps, currentStepIndex) => {
        for (let index = currentStepIndex + 1; index < steps.length; index++) {
            const step = steps.at(index);
            if (step.options.skipPoint) {
                return step.id;
            }
        }
        // if there isn't next step with skipPoint set to true, then return last step id.
        return steps.at(steps.length - 1).id;
    };

    /**
     * Checks if the previous flow is disabled for the <code>stepDescription</code>.
     * @param {*} stepDescription - The step to be checked.
     * @return {boolean} - Returns true if the previous flow is disabled, otherwise false.
     * @private
     */
    this._isDisablePreviousFlow = (stepDescription) => {
        return this._isDefined(stepDescription.disablePreviousFlow) ? stepDescription.disablePreviousFlow : false;
    };

    /**
     * Checks if the next flow is disabled for the <code>stepDescription</code>.
     * @param {*} stepDescription - The step to be checked.
     * @return {boolean} - Returns true if the previous flow is disabled, otherwise false.
     * @private
     */
    this._isDisableNextFlow = (stepDescription) => {
        return this._isDefined(stepDescription.disableNextFlow) ? stepDescription.disableNextFlow : false;
    };

    /**
     * Pauses current ran guide.
     * @param {Shepherd.Tour} guide
     * @private
     */
    this._pauseGuide = (guide) => {
        guide.hide();
        const step = this._getStepWhichCanBePaused(guide.steps, guide.getCurrentStep().id);
        this._saveStep(step);
        LocalStorageAdapter.set(GUIDE_PAUSE, true);

        // Remove all step from history which can be paused and are after the step on which guide will be paused.
        let stepHistoryCleaned = false;
        while (!stepHistoryCleaned) {
            const stepHistory = this._getHistory();
            if (stepHistory && stepHistory.length > 0 && stepHistory.at(stepHistory.length - 1) !== step.options.id) {
                this._removeLastStepFromHistory();
                continue;
            }
            stepHistoryCleaned = true;
        }

        if (this.onPause()) {
            this.onPause();
        }
    };

    this._getStepWhichCanBePaused = (steps, stepId) => {
        const index = steps.findIndex((step) => step.id === stepId);
        const step = steps[index];
        if (step.options.canBePaused) {
            return step;
        }
        if (index > 0) {
            return this._getStepWhichCanBePaused(steps, steps[index - 1].id);
        }
        return undefined;
    };

    /**
     * Creates a previous button.
     * @param {Shepherd.Tour} guide - the guide.
     * @return {*} created button.
     * @private
     */
    this._getPreviousButton = (guide) => {
        const text = $translate.instant('previous.btn');
        const action = this._getPreviousButtonAction(guide);
        const prevButton = this._getButton(text, action);
        prevButton.classes += ' shepherd-prev-button';
        return prevButton;
    };

    this._getPreviousButtonAction = (guide) => {
        return () => {
            const nextStepId = this._getPreviousStepIdFromHistory();
            let nextStep;

            if (nextStepId === -1) {
                nextStep = guide.steps.at(0);
            } else {
                nextStep = guide.steps.find((step) => step.options.id === nextStepId);
            }

            const currentStep = guide.getCurrentStep();

            currentStep.hide();

            currentStep.options.initPreviousStep({GuideUtils, ShepherdService: this}, currentStep.options.id)
                .then(() => {
                    if (this._isFunction(currentStep.options.onPreviousClick)) {
                        currentStep.options.onPreviousClick(guide)
                            .then((stopStepNavigation) => {
                                if (!this._isDefined(stopStepNavigation) || stopStepNavigation === null || !stopStepNavigation) {
                                    currentStep.hide();
                                    guide.show(nextStep.id);
                                }
                            })
                            .catch((error) => {
                                logger.error(error);
                                this._abortGuide(guide);
                            });
                        return;
                    } else if (nextStep.options.forceReload || nextStep.options.url && nextStep.options.url !== currentStep.options.url) {
                        if (nextStep.options.url && getPathName() !== nextStep.options.url) {
                            navigate(nextStep.options.url);
                        }
                    }
                    currentStep.hide();
                    guide.show(nextStep.id);
                })
                .catch((error) => {
                    logger.error(error);
                    this._abortGuide(guide);
                });
        };
    };

    /**
     * Creates a next button.
     * @param {Shepherd.Tour} guide - the guide.
     * @param {*} currentStepDescription - processed step description.
     * @param {*} nextStepDescription - next step description.
     * @return {*} created step.
     * @private
     */
    this._getNextButton = (guide, currentStepDescription, nextStepDescription) => {
        const text = $translate.instant('next.btn');
        const action = _getNextButtonAction(guide, currentStepDescription, nextStepDescription);
        const nextButton = this._getButton(text, action);
        nextButton.classes += ' shepherd-next-button';
        return nextButton;
    };

    const _getNextButtonAction = (guide, currentStepDescription, nextStepDescription) => {
        return () => {
            currentStepDescription.onNextValidate(currentStepDescription)
                .then((valid) => {
                    if (valid) {
                        if (this._isFunction(currentStepDescription.onNextClick)) {
                            const onNextResult = currentStepDescription.onNextClick(guide, currentStepDescription);
                            if (onNextResult instanceof Promise) {
                                onNextResult.catch(() => this._abortGuide(guide));
                            }
                        } else {
                            if (nextStepDescription.url && (nextStepDescription.forceReload || nextStepDescription.url !== currentStepDescription.url)) {
                                navigate(nextStepDescription.url);
                            }
                            guide.next();
                        }
                    }
                });
        };
    };

    /**
     * Updates guide related values into local store.
     * @private
     */
    this._updateLocalStorage = () => {
        const activeTour = Shepherd.activeTour;
        if (!activeTour) {
            return;
        }
        const currentStep = activeTour.getCurrentStep();

        if (currentStep.options.skipFromHistory) {
            logger.log('Current step: ', currentStep.options.skipFromHistory);
            return;
        }

        const currentStepIndex = activeTour.steps.findIndex((step) => step.id === currentStep.id);

        const lastSavedStepId = this.getCurrentStepId();
        const lastSavedStepIndex = activeTour.steps.findIndex((step) => step.id === lastSavedStepId);

        // If current step index is greater than last saved step index this means that next button was clicked.
        if (currentStepIndex > lastSavedStepIndex) {
            this._addStepToHistory(currentStep);
        } else if (lastSavedStepIndex > currentStepIndex) {
            this._removeLastStepFromHistory();
        }

        this._saveStep(currentStep);
    };

    this._saveStep = (step) => {
        if (step) {
            LocalStorageAdapter.set(GUIDE_ID, step.tour.options.name);
            LocalStorageAdapter.set(GUIDE_CURRENT_STEP_ID, step.options.id);
        }
    };

    this._addStepToHistory = (step) => {
        const history = this._getHistory();
        history.push(step.options.id);
        LocalStorageAdapter.set(GUIDE_STEP_HISTORY, history);
    };

    this._getHistory = () => {
        const stepHistory = LocalStorageAdapter.get(GUIDE_STEP_HISTORY);
        if (stepHistory) {
            return stepHistory;
        }
        return [];
    };

    this._removeLastStepFromHistory = () => {
        const history = this._getHistory();
        history.splice(history.length - 1, 1);
        LocalStorageAdapter.set(GUIDE_STEP_HISTORY, history);
    };

    this._getPreviousStepIdFromHistory = (stepId) => {
        const history = this._getHistory() || [];
        if (stepId) {
            const index = history.findIndex((historyStepId) => historyStepId === stepId);
            if (index > 0) {
                return history.at(index - 1);
            }
        } else {
            if (history.length > 1) {
                return history.at(history.length - 2);
            }
        }
        return -1;
    };

    this.getPreviousStepFromHistory = (stepId) => {
        const previousStepId = this._getPreviousStepIdFromHistory(stepId);
        return Shepherd.activeTour.steps.find((step) => step.id === previousStepId);
    };

    /**
     * Remove all related data to a guid from local store.
     * @private
     */
    this._clearLocalStorage = () => {
        LocalStorageAdapter.remove(GUIDE_ID);
        LocalStorageAdapter.remove(GUIDE_PAUSE);
        LocalStorageAdapter.remove(GUIDE_CURRENT_STEP_ID);
        LocalStorageAdapter.remove(GUIDE_STEP_HISTORY);
    };

    /**
     *
     * Creates a Shepherd Tour step and fill it with base properties from <code>stepDescription</code>.
     * @param {Shepherd.Tour} guide
     * @param {*} $translate
     * @param {*} stepDescription
     * @param {function} onShow - function which will be executed when popup dialog shown.
     * @return {*}
     * @private
     */
    this._toBaseGuideStep = (guide, $translate, stepDescription, onShow) => {
        let attachTo;
        if (stepDescription.elementSelector) {
            attachTo = {
                element: stepDescription.elementSelector,
                on: stepDescription.placement,
            };
        }

        const title = GuideUtils.unescapeHtml(GuideUtils.translateLocalMessage($translate, $interpolate, stepDescription.title, stepDescription));

        // Adds " - n/N" to titles if the step is part of a multistep process,
        // where n is the current step number and N is the total number of steps
        let extraTitle = '';
        if (stepDescription.stepsTotalN > 1) {
            const progress = document.createElement('span');
            progress.setAttribute('gdb-tooltip', $translate.instant('guide.block.progress.tooltip', {action: title}));
            progress.setAttribute('tooltip-placement', 'bottom');
            progress.innerText = $translate.instant('guide.block.progress', {
                n: stepDescription.stepN + 1,
                nn: stepDescription.stepsTotalN,
            });
            extraTitle = '&nbsp;&mdash;&nbsp;' + progress.outerHTML;
        }

        const text = () => {
            const content = this._toParagraph(GuideUtils.translateLocalMessage($translate, $interpolate, stepDescription.content, stepDescription));

            let extraContent = '';
            if (stepDescription.extraContent) {
                extraContent = GuideUtils.translateLocalMessage($translate, $interpolate, stepDescription.extraContent, stepDescription);
                extraContent = this._toParagraph(extraContent, stepDescription.extraContentClass);
            }
            return content + extraContent;
        };

        const clickable = stepDescription.type !== 'readonly';

        const extraPadding = stepDescription.extraPadding ? stepDescription.extraPadding : 0;

        const step = {
            id: stepDescription.id,
            title: title + extraTitle,
            text,
            url: stepDescription.url,
            maxWaitTime: stepDescription.maxWaitTime,
            cancelIcon: {enabled: true},
            attachTo,
            modalOverlayOpeningPadding: extraPadding,
            modalOverlayOpeningRadius: 0,
            canBePaused: stepDescription.canBePaused,
            advanceOn: stepDescription.advanceOn,
            showOn: stepDescription.showOn,
            scrollToHandler: stepDescription.scrollToHandler,
            classes: `guide-dialog ${stepDescription.class}-guide-dialog`,
            beforeShowPromise: this._getBeforeShowPromise(guide, stepDescription),
            canClickTarget: clickable,
            keyboardNavigation: false,
            skipPoint: stepDescription.skipPoint,
            skipButtonLabel: stepDescription.skipButtonLabel,
            skipFromHistory: stepDescription.skipFromHistory,
            onPreviousClick: stepDescription.onPreviousClick,
            initPreviousStep: stepDescription.initPreviousStep,
            forceReload: stepDescription.forceReload,
            isLastStep: !!stepDescription.lastStep,
            allowScroll: stepDescription.allowScroll,
            when: {
                show: this._getShowFunction(guide, stepDescription, onShow),
            },
        };

        if (this._isFunction(stepDescription.hide)) {
            step.when.hide = stepDescription.hide(guide, stepDescription);
        }

        return step;
    };

    this._getBeforeShowPromise = (guide, stepDescription) => {
        if (this._isFunction(stepDescription.beforeShowPromise)) {
            return () => {
                return stepDescription.beforeShowPromise(guide, stepDescription);
            };
        }
    };

    this._getShowFunction = (guide, stepDescription, onShow) => {
        if (this._isFunction(stepDescription.show)) {
            return () => {
                stepDescription.show(guide, stepDescription)();
                onShow();
                this._whenStepShow(stepDescription);
            };
        }

        return () => {
            onShow();
            this._whenStepShow(stepDescription);
        };
    };

    this._whenStepShow = (stepDescription) => {
        const currentStep = Shepherd.activeTour.getCurrentStep();
        const currentStepElement = currentStep.getElement();

        this._addTotalProgress(currentStep, currentStepElement);

        this._addTypeIcon(currentStepElement, stepDescription);
    };

    this._toParagraph = (text, textClass) => {
        if (text) {
            const p = document.createElement('p');
            if (textClass) {
                p.className = textClass;
            }
            p.innerHTML = text;
            return p.outerHTML;
        } else {
            return '';
        }
    };

    this._addTotalProgress = (currentStep, currentStepElement) => {
        const steps = Shepherd.activeTour.steps;
        const content = currentStepElement.querySelector('.shepherd-content');
        const progress = document.createElement('div');
        progress.className = 'shepherd-progress';
        const progressText = document.createElement('span');
        progressText.className = 'text-muted';
        progressText.innerText = $translate.instant('guide.total.progress', {n: steps.indexOf(currentStep) + 1, nn: steps.length});
        progressText.setAttribute('gdb-tooltip', $translate.instant('guide.total.progress.tooltip'));
        progressText.setAttribute('tooltip-placement', 'left');
        progress.appendChild(progressText);

        content.appendChild(progress);
    };

    this._addTypeIcon = (currentStepElement, stepDescription) => {
        let iconName;
        let iconTooltip;
        switch (stepDescription.type) {
            case 'clickable':
                iconName = 'focus';
                iconTooltip = 'mouse';
                break;
            case 'input':
                iconName = 'edit';
                iconTooltip = 'input';
                break;
            default:
                // handles 'readonly' and future types gracefully
                iconName = 'info';
                iconTooltip = 'info';
                break;
        }

        const typeIcon = document.createElement('span');
        typeIcon.className = 'shepherd-step-type icon-' + iconName + ' icon-1-25x';
        typeIcon.setAttribute('gdb-tooltip', $translate.instant('guide.step-type.' + iconTooltip));
        typeIcon.setAttribute('tooltip-placement', 'bottom-right');

        const header = currentStepElement.querySelector('.shepherd-header');
        if (header && typeIcon) {
            header.insertBefore(typeIcon, header.firstChild);
        }
    };

    /**
     * Created a base button.
     * @param {string} text - the text of button.
     * @param {function} action - to be executed when button is clicked.
     * @param {boolean} isSecondary - if true will be styled as secondary button.
     * @return {*} created button.
     */
    this._getButton = (text, action, isSecondary) => {
        const button = {
            text: text,
            classes: isSecondary ? 'btn btn-secondary' : 'btn btn-primary',
        };

        button.action = ($event) => {
            $event.stopPropagation();
            action();
        };

        return button;
    };

    /**
     * Checks whether the given value is a function.
     *
     * @param {*} value - The value to check.
     * @returns {boolean} `true` if the value is a function; otherwise, `false`.
     */
    this._isFunction = (value) => {
        return typeof value === 'function';
    };

    /**
     * Checks whether the given value is defined (not `undefined`).
     *
     * @param {*} value - The value to check.
     * @returns {boolean} `true` if the value is defined; otherwise, `false`.
     */
    this._isDefined = (value) => {
        return typeof value !== 'undefined';
    };
}
